import { GO_LESSONS, compileScene, figureWidthFor, taughtElementOf } from './lessons/index.js';
import type { GoLesson, LessonScene } from './lessons/index.js';
import { renderSvg } from './svg.js';

/**
 * The lesson reading surface: one scene at a time, at a figure width that is
 * one of the three the tests compile at, so what is screenshotted is what is
 * verified. Every scene shows the same five things in the same order — claim,
 * source, captured output, the determinism the capture declared, and the one
 * compiled figure — because a lesson that rearranges itself per scene is
 * teaching the reader the layout instead of the subject.
 */

interface Position {
  lesson: number;
  scene: number;
}

export function renderGoLessons(root: HTMLElement): void {
  const params = new URLSearchParams(window.location.search);
  const position: Position = {
    lesson: Math.max(0, GO_LESSONS.findIndex((lesson) => lesson.id === params.get('lesson'))),
    scene: Math.max(0, Number(params.get('scene') ?? '1') - 1),
  };

  const draw = (): void => {
    const lesson = GO_LESSONS[position.lesson] ?? GO_LESSONS[0]!;
    position.scene = Math.min(Math.max(0, position.scene), lesson.scenes.length - 1);
    const scene = lesson.scenes[position.scene]!;
    const width = figureWidthFor(window.innerWidth);
    const figure = compileScene(scene, width);
    const taught = taughtElementOf(scene.spec(width));

    root.innerHTML = `<main class="lsn-shell" data-lesson="${lesson.id}" data-scene="${scene.id}">
      <header class="lsn-top">
        <a class="lsn-back" href="./">← Foundation gallery</a>
        <span class="lsn-toolchain">${escapeHtml(lesson.toolchain)}</span>
      </header>

      <nav class="lsn-lessons" aria-label="Lessons">
        ${GO_LESSONS.map(
          (item, index) => `<button data-goto-lesson="${index}" class="${index === position.lesson ? 'is-current' : ''}">
            <small>${escapeHtml(item.module)}</small>
            <b>${escapeHtml(item.title)}</b>
            <span>${escapeHtml(item.premise)}</span>
          </button>`,
        ).join('')}
      </nav>

      <section class="lsn-stage">
        <div class="lsn-rail" role="tablist" aria-label="Scenes">
          ${lesson.scenes
            .map(
              (item, index) => `<button role="tab" aria-selected="${index === position.scene}" data-goto-scene="${index}"
                class="${index === position.scene ? 'is-current' : ''}${index < position.scene ? ' is-done' : ''}"
                title="${escapeHtml(item.heading)}"><i>${index + 1}</i><span>${escapeHtml(beatLabel(item))}</span></button>`,
            )
            .join('')}
        </div>

        <div class="lsn-progress"><span>${escapeHtml(lesson.module)}</span><span>${position.scene + 1} / ${lesson.scenes.length}</span></div>

        <div class="lsn-copy">
          <div class="lsn-stage-label">${escapeHtml(scene.stageLabel)}</div>
          <h1>${escapeHtml(scene.heading)}</h1>
          <p>${escapeHtml(scene.subtitle)}</p>
        </div>

        <div class="lsn-evidence">
          <section class="lsn-card">
            <header><span>${escapeHtml(scene.code.caption)}</span><i>source</i></header>
            <pre>${lines(scene.code.text)}</pre>
          </section>
          <section class="lsn-card lsn-card-output">
            <header><span>${escapeHtml(scene.evidence.caption)}</span><i>captured</i></header>
            <pre>${lines(scene.evidence.text)}</pre>
          </section>
        </div>

        <div class="lsn-determinism"><b>Determinism</b><span>${escapeHtml(scene.determinism)}</span></div>

        <figure class="lsn-figure">
          <figcaption>
            <b>${escapeHtml(figure.title)}</b>
            <span>${escapeHtml(figure.subtitle)}</span>
          </figcaption>
          <div class="lsn-plate" style="width:${figure.viewport.width}px">${renderSvg(figure, 'kiln-exact')}</div>
          <div class="lsn-figure-meta">
            <span>${escapeHtml(figure.family)} · ${escapeHtml(figure.view)} view</span>
            <span>${figure.shapes.length} shapes · ${figure.hitRegions.length} touch targets · ${figure.relations.length} relations</span>
            <span>taught: <code>${escapeHtml(taught ?? '—')}</code></span>
            <span>geometry <code>${figure.geometryFingerprint}</code></span>
          </div>
        </figure>

        ${figure.warnings.length ? `<div class="lsn-warnings">${figure.warnings.map((item) => `<span>${escapeHtml(item)}</span>`).join('')}</div>` : ''}

        <div class="lsn-why"><b>Why this figure</b><span>${escapeHtml(scene.figureReason)}</span></div>
        <div class="lsn-provenance"><b>Truth source</b><span>${escapeHtml(figure.provenance)}</span></div>

        <footer class="lsn-actions">
          <button data-step="-1"${position.lesson === 0 && position.scene === 0 ? ' disabled' : ''}>← Back</button>
          <button data-step="1" class="is-primary">${nextLabel(lesson, position)}</button>
        </footer>
      </section>
    </main>`;

    root.querySelectorAll<HTMLButtonElement>('[data-goto-lesson]').forEach((button) =>
      button.addEventListener('click', () => {
        position.lesson = Number(button.dataset.gotoLesson);
        position.scene = 0;
        draw();
      }),
    );
    root.querySelectorAll<HTMLButtonElement>('[data-goto-scene]').forEach((button) =>
      button.addEventListener('click', () => {
        position.scene = Number(button.dataset.gotoScene);
        draw();
      }),
    );
    root.querySelectorAll<HTMLButtonElement>('[data-step]').forEach((button) =>
      button.addEventListener('click', () => {
        step(position, Number(button.dataset.step));
        draw();
      }),
    );
  };

  // Debug hook, matching the `__goto(n)` convention in design/core.md: the
  // screenshot gate drives every scene of every lesson through one page.
  (window as unknown as { __gotoScene?: (lesson: number, scene: number) => void }).__gotoScene = (lesson, scene) => {
    position.lesson = Math.min(Math.max(0, lesson), GO_LESSONS.length - 1);
    position.scene = Math.max(0, scene);
    draw();
  };
  (window as unknown as { __sceneCount?: (lesson: number) => number }).__sceneCount = (lesson) =>
    GO_LESSONS[Math.min(Math.max(0, lesson), GO_LESSONS.length - 1)]!.scenes.length;
  (window as unknown as { __lessonCount?: number }).__lessonCount = GO_LESSONS.length;

  window.addEventListener('resize', draw);
  draw();
}

function step(position: Position, direction: number): void {
  const lesson = GO_LESSONS[position.lesson]!;
  const next = position.scene + direction;
  if (next >= 0 && next < lesson.scenes.length) {
    position.scene = next;
    return;
  }
  if (direction > 0) {
    position.lesson = (position.lesson + 1) % GO_LESSONS.length;
    position.scene = 0;
    return;
  }
  position.lesson = (position.lesson - 1 + GO_LESSONS.length) % GO_LESSONS.length;
  position.scene = GO_LESSONS[position.lesson]!.scenes.length - 1;
}

function nextLabel(lesson: GoLesson, position: Position): string {
  return position.scene === lesson.scenes.length - 1 ? 'Next lesson →' : 'Continue →';
}

const BEAT_LABELS: Record<string, string> = {
  symptom: 'Symptom',
  mechanism: 'Mechanism',
  choice: 'Choice',
  fix: 'Fix',
  trace: 'Trace',
  evidence: 'Evidence',
  'trade-off': 'Trade-off',
  production: 'Production',
  model: 'Model',
};

function beatLabel(scene: LessonScene): string {
  return BEAT_LABELS[scene.beat] ?? scene.beat;
}

/**
 * One block per captured line, so a line that has to wrap keeps a hanging
 * indent of its own instead of the whole card sharing one first-line indent.
 * Nothing is ever cut: a long line wraps, it does not scroll out of sight.
 */
function lines(value: string): string {
  return value.split('\n').map((line) => `<span>${escapeHtml(line)}</span>`).join('');
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character]!);
}
